import type { SegmentOverride, SegmentWithOverride } from '../CanvasView/useCanvasTranslation';

export type EditableOverride = SegmentOverride;

export type EditableSegment = SegmentWithOverride & {
  // Override value if set, else machine value, else empty.
  draftValue: string;
};

// 'english-shown': no translation exists, the learner sees English.
// 'needs-re-review': override kept but its English source has drifted.
export type OverrideState = 'human' | 'machine' | 'english-shown' | 'needs-re-review';

export type SegmentGroup = {
  // Unique per group; conversation groups key on the node, page groups on
  // document position (two sections may share a heading).
  groupId: string;
  nodeId: string | null;
  // Nearest preceding page heading; null for conversations.
  sectionHeading: string | null;
  // Feeds the breadcrumb that disambiguates identical replies.
  parentQuestion: string | null;
  segments: EditableSegment[];
};

// Relies on walk emit order: the node's tiptap body precedes its reply titles,
// so the first tiptap segment is the question.
export function deriveParentQuestion(segments: EditableSegment[]): string | null {
  const firstTiptap = segments.find(
    ({ segment }) => segment.locator.kind === 'CONVERSATION_SENTENCE',
  );
  return firstTiptap?.segment.sourceText ?? null;
}

// The outline/header display name; null falls back to the generic label.
export function groupLabel(group: SegmentGroup): string | null {
  return group.parentQuestion ?? group.sectionHeading;
}

// Preserves extract order so rows read top to bottom as in the document.
// Groups are consecutive runs: conversations break on node id (a node's
// segments are contiguous in walk order), pages break on section heading.
export function groupSegments(rows: EditableSegment[]): SegmentGroup[] {
  const groups: SegmentGroup[] = [];
  let current: SegmentGroup | null = null;

  for (const row of rows) {
    const nodeId = row.segment.nodeId;
    const sectionHeading = row.segment.sectionHeading;
    if (
      current === null ||
      current.nodeId !== nodeId ||
      current.sectionHeading !== sectionHeading
    ) {
      current = {
        groupId: nodeId != null ? `node:${nodeId}` : `section:${groups.length}`,
        nodeId,
        sectionHeading,
        parentQuestion: null,
        segments: [],
      };
      groups.push(current);
    }
    current.segments.push(row);
  }

  return groups.map((group) => ({
    ...group,
    parentQuestion: deriveParentQuestion(group.segments),
  }));
}

// Matches the two strings the author can see: the English source and the value
// shown in the translation column (draft, which defaults to override/machine).
export function rowMatchesQuery(row: EditableSegment, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') {
    return true;
  }
  return (
    row.segment.sourceText.toLowerCase().includes(needle) ||
    row.draftValue.toLowerCase().includes(needle)
  );
}

export function isQuestionRow(row: EditableSegment): boolean {
  return row.segment.locator.kind === 'CONVERSATION_SENTENCE';
}

export function overrideStateFor(row: EditableSegment): OverrideState {
  if (row.override !== undefined) {
    return row.override.isStale ? 'needs-re-review' : 'human';
  }
  const hasMachineValue = (row.segment.currentMachineValue ?? '').trim() !== '';
  return hasMachineValue ? 'machine' : 'english-shown';
}

export function isDirty(row: EditableSegment): boolean {
  const saved = row.override?.overrideValue ?? row.segment.currentMachineValue ?? '';
  const draft = row.draftValue.trim();
  if (draft === saved.trim()) {
    return false;
  }
  // Clearing counts only when it removes an override; on a machine-only row it's a no-op.
  if (draft === '') {
    return row.override !== undefined;
  }
  return true;
}

export type ProvenanceCounts = {
  human: number;
  machine: number;
  englishShown: number;
  needsReReview: number;
};

export function countProvenance(groups: SegmentGroup[]): ProvenanceCounts {
  const counts: ProvenanceCounts = { human: 0, machine: 0, englishShown: 0, needsReReview: 0 };
  for (const group of groups) {
    for (const row of group.segments) {
      switch (overrideStateFor(row)) {
        case 'human':
          counts.human += 1;
          break;
        case 'machine':
          counts.machine += 1;
          break;
        case 'english-shown':
          counts.englishShown += 1;
          break;
        case 'needs-re-review':
          counts.needsReReview += 1;
          break;
      }
    }
  }
  return counts;
}

export function worstStateForGroup(group: SegmentGroup): OverrideState {
  let worst: OverrideState = 'human';
  const rank: Record<OverrideState, number> = {
    'needs-re-review': 3,
    'english-shown': 2,
    machine: 1,
    human: 0,
  };
  for (const row of group.segments) {
    const state = overrideStateFor(row);
    if (rank[state] > rank[worst]) {
      worst = state;
    }
  }
  return worst;
}

// The first group carrying anything the author should look at (a drifted override
// or an untranslated segment), independent of any active filter or search: "Next
// to review" targets this and reveals it even when the current view hides it.
export function nextReviewTargetGroupId(groups: SegmentGroup[]): string | null {
  const target = groups.find((group) => {
    const worst = worstStateForGroup(group);
    return worst === 'needs-re-review' || worst === 'english-shown';
  });
  return target?.groupId ?? null;
}
